import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { RelativeTime } from '@bonusly/common-relative-time';
import { Box, chakra, HStack, Spacer, StyleProps, Text, Tooltip, useDisclosure, XOutline } from '@bonusly/design-system';

import { useIsOverflow } from '../../hooks/use-is-overflow';
import { ReferencedRecognition } from '../../lib';

interface RecognitionPreviewProps extends StyleProps {
	linkToReference?: boolean;
	onRemove?: VoidFunction;
	reference: ReferencedRecognition;
}

const StyledLink = chakra(Link, { _hover: { textDecoration: 'underline' } });

export const RecognitionPreview: FC<RecognitionPreviewProps> = ({ linkToReference = false, onRemove, reference, ...rest }) => {
	const [textRef, setTextRef] = useState<HTMLParagraphElement | null>(null);
	const { t } = useTranslation();
	const preview = useDisclosure();
	const isOverflow = useIsOverflow(textRef);

	return (
		<Box borderColor="border.default" borderRadius="sm" borderStyle="solid" borderWidth="1px" data-testid="recognition-preview" pb={4} pt={3} px={4} {...rest}>
			<HStack mb={2}>
				<Text color="text.softest" size="sm">
					{t('feedback:reference.recognition', { name: reference.giver.name })} | <RelativeTime date={reference.date} fontSize="sm" />
					{linkToReference && (
						<>
							{' '}
							|{' '}
							<StyledLink _hover={{ textDecor: 'underline' }} to={`/bonuses/${reference.id}`}>
								{t('feedback:reference.view_recognition')}
							</StyledLink>
						</>
					)}
				</Text>
				<Spacer />
				{onRemove && (
					<Tooltip label={t('feedback:reference.remove')}>
						<Box
							aria-label={t('feedback:reference.remove')}
							as="button"
							boxSize={5}
							borderRadius="full"
							bg="secondary.bg"
							data-testid="remove-reference"
							onClick={onRemove}
							transitionDuration="normal"
							transitionProperty="background-color"
							_hover={{
								bg: 'secondary.bgHover',
							}}
						>
							<XOutline color="text.onColor" size="xs" />
						</Box>
					</Tooltip>
				)}
			</HStack>

			<Text noOfLines={preview.isOpen ? undefined : 3} ref={setTextRef}>
				{reference.text}
			</Text>

			{(isOverflow || preview.isOpen) && (
				<Box as="button" color="blue.500" display="inline" fontSize="md" onClick={preview.onToggle} type="button">
					{preview.isOpen ? t('common:show_less') : t('common:show_more')}
				</Box>
			)}
		</Box>
	);
};
